import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, ErrorState, Spinner } from 'escrowbuild-ui';

import {
  useT,
  type AppTranslate,
  type AppTranslationKey,
  type TranslationParams,
} from '@/i18n';
import { ExchangeFailure, exchangeCode, takeStateNonce } from '@/lib/auth';
import { useSession } from '@/lib/session';
import { usePageTitle } from '@/app/usePageTitle';

/* =============================================================================
   The provider round trip's landing
   -----------------------------------------------------------------------------
   The API 302s here with `?code=` (or `?e=` on failure) once consent is done.
   The code is a ONE-TIME, short-lived credential, and almost everything below
   exists because of that one fact:

     - it is claimed exactly once, so StrictMode's dev remount cannot burn it on
       a second exchange;
     - it is stripped from the address bar before anything is awaited, so a
       refresh or a back-navigation cannot replay it;
     - the nonce it arrives with is compared to the one this tab sent, so a code
       from another tab's flow is refused rather than exchanged.
   ============================================================================= */

type Phase = 'working' | 'failed' | 'empty';

/**
 * Why the round trip did not produce a session.
 *
 * A KEY rather than the finished sentence, because the reason is decided inside
 * an effect that must run once and the sentence has to be able to change
 * language afterwards — a reader who switches to Portuguese while this screen is
 * up should not be left reading the failure in English. Text the API supplied is
 * carried through as it arrived, since it is not ours to translate.
 */
type Failure =
  | { kind: 'key'; key: AppTranslationKey; params: TranslationParams }
  | { kind: 'text'; text: string };

function failureMessage(failure: Failure, t: AppTranslate): string {
  return failure.kind === 'key' ? t(failure.key, failure.params) : failure.text;
}

/**
 * The claimed round trip, held at module scope.
 *
 * StrictMode mounts, unmounts and mounts this component again in development,
 * and by then the code is out of the URL — so it cannot live in component
 * state, which the unmount destroys.
 *
 * It belongs to the DOCUMENT and is therefore never cleared: measured, an
 * earlier version cleared it on the branches that do not await, and the second
 * mount — which is the live one — found nothing and painted "No sign-in result"
 * over a `?e=` failure it had already decided.
 */
type Claim =
  | { kind: 'code'; value: string; verified: boolean }
  | { kind: 'outcome'; value: string };

let claim: Claim | null = null;

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { grant } = useSession();
  const [phase, setPhase] = useState<Phase>('working');
  const [failure, setFailure] = useState<Failure | null>(null);
  const t = useT();
  usePageTitle();

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const outcome = params.get('e');

    if (claim === null && (code || outcome)) {
      const sent = takeStateNonce();
      claim = code
        ? { kind: 'code', value: code, verified: sent !== null && sent === params.get('state') }
        : { kind: 'outcome', value: outcome ?? '' };

      /*
         BEFORE any await — this is the whole point of doing it here rather than
         after the exchange. The code is a bearer credential for a session, and
         a URL is something a person reloads or navigates back into; leaving it
         in the address bar is what turns one login into a replay. `replaceState`
         and not a push, so "back" leaves the callback rather than returning to
         the code.
      */
      window.history.replaceState({}, '', window.location.pathname);
    }

    const claimed = claim;

    async function run() {
      if (claimed === null) {
        setPhase('empty');
        return;
      }

      if (claimed.kind === 'outcome') {
        setFailure(
          claimed.value
            ? { kind: 'key', key: 'auth.providerReturned', params: { reason: claimed.value } }
            : { kind: 'key', key: 'auth.notCompleted', params: {} },
        );
        setPhase('failed');
        return;
      }

      if (!claimed.verified) {
        // Not exchanged at all: the API echoed a nonce this tab never sent, so
        // the code is refused rather than traded for a session.
        setFailure({ kind: 'key', key: 'auth.unverified', params: {} });
        setPhase('failed');
        return;
      }

      try {
        const envelope = await exchangeCode(claimed.value);
        if (cancelled) return;

        if (envelope.result === 'success' && envelope.access && envelope.refresh && envelope.user) {
          grant(envelope.access, envelope.refresh, envelope.user);
          navigate('/dashboard', { replace: true });
          return;
        }
        const reported = envelope.message ?? envelope.reason;
        setFailure(
          reported
            ? { kind: 'text', text: reported }
            : { kind: 'key', key: 'auth.notCompleted', params: {} },
        );
        setPhase('failed');
      } catch (error) {
        if (cancelled) return;
        /*
           A failure `exchangeCode` decided carries its sentence as a key, so a
           reader who switches language while this screen is up reads the failure
           in the language they chose. Anything else arrived as text — and only
           the envelope's own `message` and `reason` reach here that way, which
           are the API's words and stay exactly as it sent them.
        */
        setFailure(
          error instanceof ExchangeFailure
            ? { kind: 'key', key: error.key, params: error.status === undefined ? {} : { status: error.status } }
            : { kind: 'text', text: error instanceof Error ? error.message : String(error) },
        );
        setPhase('failed');
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [grant, navigate]);

  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-5">
      {phase === 'working' ? (
        <>
          <h1 className="dhd-sr-only">{t('auth.signingInHeading')}</h1>
          <div className="flex flex-col items-center gap-4">
            <Spinner size="lg" label={t('auth.signingIn')} />
            <p className="font-display text-sm font-semibold tracking-label text-heading uppercase">
              {t('auth.signingIn')}
            </p>
          </div>
        </>
      ) : (
        <div className="flex w-full max-w-xl flex-col items-center gap-6">
          <h1 className="dhd-sr-only">
            {phase === 'failed' ? t('auth.failedTitle') : t('auth.emptyTitle')}
          </h1>
          <ErrorState
            variant="page"
            title={phase === 'failed' ? t('auth.failedTitle') : t('auth.emptyTitle')}
            message={failure ? failureMessage(failure, t) : t('auth.emptyMessage')}
          />
          <Button variant="primary" onClick={() => navigate('/', { replace: true })}>
            {t('auth.backToSignIn')}
          </Button>
        </div>
      )}
    </main>
  );
}
